// src/components/VencimientosTab.js

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { formatCurrency, formatNumberForDisplay } from '../utils/formatters';
import { compareValues } from '../utils/sortHelpers';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useFechaHoy } from '../hooks/useFechaHoy';
import { fromIsoDate } from '../utils/dateHelpers';
import { useStackedTable } from '../hooks/useStackedTable';
import TopScrollbar from './TopScrollbar';

const TabContent = styled.div``;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-weight: 700;
`;

const DateGroup = styled.div`
  margin-bottom: 3rem;
`;

const DateTitle = styled.h3`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.primary};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  padding-bottom: 0.75rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${props => props.theme.typography.bodyFamily};

  th, td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    white-space: nowrap;
  }

  th {
    font-family: ${props => props.theme.typography.titleFamily};
    font-weight: 600;
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    user-select: none;
    
    &:hover {
        color: ${props => props.theme.colors.primary};
    }
  }
  
  tbody tr:hover {
    background-color: ${props => props.theme.colors.background};
  }

  tfoot td {
    font-weight: 700;
    color: ${props => props.theme.colors.text};
    border-top: 2px solid ${props => props.theme.colors.primary};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const SortArrow = styled.span`
  margin-left: 8px;
  font-size: 0.8em;
  color: ${props => props.theme.colors.primary};
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  font-family: ${props => props.theme.typography.bodyFamily};
  font-size: 0.9rem;
  min-width: 220px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

// Tabla de un grupo de fecha con su propia barra de desplazamiento superior.
const VencimientosDateGroup = ({ group, requestSort, getSortArrow }) => {
    const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
        useHorizontalScrollSync([group.vencimientos]);
    const stackedTable = useStackedTable({
        title: 'Cliente',
        primary: ['Tipo', 'Operación', 'Tasa', 'Monto Operado', 'Días'],
    });

    return (
        <DateGroup>
            <DateTitle>{group.date}</DateTitle>
            <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />
            <TableWrapper ref={contentRef} onScroll={onContentScroll}>
                <StyledTable ref={stackedTable.ref} className={stackedTable.className}>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('tipo')}>Tipo {getSortArrow('tipo')}</th>
                            <th onClick={() => requestSort('createdAt')}>Fecha de Emisión {getSortArrow('createdAt')}</th>
                            <th onClick={() => requestSort('dias')}>Días {getSortArrow('dias')}</th>
                            <th onClick={() => requestSort('operacion')}>Operación {getSortArrow('operacion')}</th>
                            <th onClick={() => requestSort('cliente')}>Cliente {getSortArrow('cliente')}</th>
                            <th onClick={() => requestSort('comercial')}>Comercial {getSortArrow('comercial')}</th>
                            <th onClick={() => requestSort('operador')}>Operador {getSortArrow('operador')}</th>
                            <th onClick={() => requestSort('tasa')} style={{ textAlign: 'right' }}>Tasa {getSortArrow('tasa')}</th>
                            <th onClick={() => requestSort('montoOperado')} style={{ textAlign: 'right' }}>Monto Operado {getSortArrow('montoOperado')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {group.vencimientos.map(vencimiento => (
                            <tr key={vencimiento.id}>
                                <td>{vencimiento.tipo === 'Pase' ? 'Pase' : 'Caución'}{vencimiento.esEnVivo ? ' (en curso)' : ''}</td>
                                <td>{vencimiento.createdAt?.toDate().toLocaleDateString('es-AR') || 'N/A'}</td>
                                <td>{vencimiento.dias ?? '-'}</td>
                                <td>{vencimiento.operacion}</td>
                                <td>{vencimiento.cliente}</td>
                                <td>{vencimiento.comercial}</td>
                                <td>{vencimiento.operador}</td>
                                <td style={{ textAlign: 'right' }}>{`${formatNumberForDisplay(vencimiento.tasa)}%`}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(vencimiento.montoOperado, vencimiento.moneda)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="7" style={{ textAlign: 'right', fontWeight: 'bold' }}>TOTALES DEL DÍA:</td>
                            <td colSpan="2" style={{ textAlign: 'right' }}>
                                {Object.entries(group.totals).map(([moneda, total]) => (
                                    <div key={moneda}>{formatCurrency(total, moneda)}</div>
                                ))}
                            </td>
                        </tr>
                    </tfoot>
                </StyledTable>
            </TableWrapper>
        </DateGroup>
    );
};

const VencimientosTab = ({ vencimientos, isLoading }) => {
    // Los "próximos vencimientos" se recortan contra hoy: sin esto, la lista
    // sigue mostrando los de ayer hasta que alguien refresca.
    const fechaHoy = useFechaHoy();
    const [sortConfig, setSortConfig] = useState({ key: 'fechaVencimiento', direction: 'ascending' });
    const [clienteFilter, setClienteFilter] = useState('');

    const sortedVencimientos = useMemo(() => {
        const term = clienteFilter.trim().toLowerCase();
        const filtered = term
            ? vencimientos.filter(v => v.cliente?.toLowerCase().includes(term))
            : vencimientos;
        const sortableItems = [...filtered];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) =>
                compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction)
            );
        }
        return sortableItems;
    }, [vencimientos, sortConfig, clienteFilter]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    
    const getSortArrow = (name) => {
        if (sortConfig.key !== name) {
            return null;
        }
        return <SortArrow>{sortConfig.direction === 'ascending' ? '▲' : '▼'}</SortArrow>;
    };

    const getGroupedVencimientos = () => {
        const today = fromIsoDate(fechaHoy);

        const upcomingVencimientos = sortedVencimientos.filter(v => {
            return v.fechaVencimiento && v.fechaVencimiento.toDate() >= today;
        });

        const grouped = upcomingVencimientos.reduce((acc, vencimiento) => {
            const dateString = vencimiento.fechaVencimiento.toDate().toLocaleDateString('es-AR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
            });
            if (!acc[dateString]) {
                acc[dateString] = [];
            }
            acc[dateString].push(vencimiento);
            return acc;
        }, {});
        
        const sortedDateKeys = Object.keys(grouped).sort((a, b) => {
            const dateA = grouped[a][0].fechaVencimiento.toDate();
            const dateB = grouped[b][0].fechaVencimiento.toDate();
            return dateA - dateB;
        });

        return sortedDateKeys.map(date => {
            const dailyVencimientos = grouped[date];
            const dailyTotals = dailyVencimientos.reduce((totals, op) => {
                const monto = parseFloat(op.montoOperado) || 0;
                totals[op.moneda] = (totals[op.moneda] || 0) + monto;
                return totals;
            }, {});

            return {
                date,
                vencimientos: dailyVencimientos,
                totals: dailyTotals
            };
        });
    };

    const groupedVencimientos = getGroupedVencimientos();

    return (
        <TabContent>
            <HeaderRow className="axon-wrap" style={{ gap: '0.75rem' }}>
                <Title className="axon-page-title">Próximos Vencimientos — Cauciones y Pases (Tomadoras)</Title>
                <SearchInput
                    className="axon-fill"
                    type="text"
                    placeholder="Buscar cliente..."
                    value={clienteFilter}
                    onChange={e => setClienteFilter(e.target.value)}
                />
            </HeaderRow>

            {isLoading ? (
                <p>Cargando vencimientos...</p>
            ) : groupedVencimientos.length > 0 ? (
                groupedVencimientos.map(group => (
                    <VencimientosDateGroup
                        key={group.date}
                        group={group}
                        requestSort={requestSort}
                        getSortArrow={getSortArrow}
                    />
                ))
            ) : (
                <p>No hay vencimientos próximos.</p>
            )}
        </TabContent>
    );
};

export default VencimientosTab;